import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  Table,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useConfirm } from '../../../hooks/useConfirm';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useTimetableVersionDataQuery } from '../../../types/planung-graphql-client-defs';
import { hexToColorOption } from '../../../utils/colorUtils';
import { useTimetableStore } from '../../TimetableGrid/TimetableProvider';
import { TimetableVersionTableType } from '../graphql/types';
import { createTimetableVersionTableColumns } from '../utils/timetableVersionTableUtils';

type TimetableVersionTeachersTableProps = {
  versionUuid: string;
  onRowSelection: (rowData: TimetableVersionTableType, index: number) => void;
  onShowLessonClick: (uuid: string) => void;
  versionTeacherUuids: string[];
  search: string;
  onRemove: (uuids: string[]) => void;
};

export const TimetableVersionTeachersTable = observer(
  ({
    onRemove,
    versionTeacherUuids,
    versionUuid,
    onRowSelection,
    onShowLessonClick,
    search,
  }: TimetableVersionTeachersTableProps) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const store = useTimetableStore();
    const { sorting, saveSorting } = useColumnsSort('timetableVersionTeachersTable');
    const { rowSelection, onRowSelectionChange } = useDefaultSelecting({ 0: true });

    const [{ data: versionData }] = useTimetableVersionDataQuery({
      variables: { versionUuid },
      pause: !versionUuid,
    });
    const teachersView = versionData?.timetableVersionData.view.teachers;
    const editorLessons = versionData?.timetableVersionData.editor?.lessons;

    const teachers: TimetableVersionTableType[] = useMemo(() => {
      return (
        teachersView
          ?.filter((s) => versionTeacherUuids.includes(s.uuid))
          .map((s) => {
            const { html, label } = hexToColorOption(s.color ?? '');

            return {
              color: {
                color: html,
                label,
              },
              count: editorLessons?.filter((l) => l.teacherUuids.includes(s.uuid)).length ?? 0,
              name: s.displayNameShort ?? `${s.firstName ?? ''} ${s.lastName}`.trim(),
              shortName: s.shortName ?? '',
              uuid: s.uuid,
            };
          }) ?? []
      );
    }, [teachersView, versionTeacherUuids, editorLessons]);

    const columns = useMemo(() => createTimetableVersionTableColumns(t('persons.name') as string), [t]);

    const canBeRemoved = (uuids: string[]) => {
      return !editorLessons?.some((l) => l.teacherUuids.some((teacherUuid) => uuids.includes(teacherUuid)));
    };

    const { confirm, ConfirmationDialog } = useConfirm();

    const handleRemove = async (uuids: string[]) => {
      const res = await confirm({
        title: t('timetableVersion.removeTeachers', { count: uuids.length }),
        message: t('timetableVersion.removeTeachersDescription', { count: uuids.length }),
      });
      if (res) {
        onRemove(uuids);
      }
    };
    const readOnly = store.readonly;

    return (
      <>
        <Table<TimetableVersionTableType>
          isDesktop={!isMobile}
          showBorderRadius
          showShadow
          canScroll
          minHeight={900}
          sorting={sorting}
          onSortingChange={saveSorting}
          columns={columns}
          data={teachers}
          globalFilter={search}
          lastColWidth='140px'
          lastCol={(row) => {
            return (
              <ButtonGroup>
                <Button hierarchy='secondary' onClick={() => onShowLessonClick(row.original.uuid)}>
                  {t('timetableVersion.showLessons')}
                </Button>
                <Dropdown
                  noPadding
                  trigger={<Button hierarchy='secondary' icon={<DotsHorizontalIcon className='small' />} />}
                >
                  <DropdownMenu
                    data={[
                      {
                        label: t('timetableVersion.editData'),
                        onClick: () => {
                          console.log('edit clicked');
                        },
                        disabled: true,
                      },
                      {
                        label: t('timetableVersion.test'),
                      },
                      {
                        label: t('timetableVersion.remove'),
                        type: 'default',
                        color: 'error',
                        disabled: !canBeRemoved([row.original.uuid]) || readOnly,
                        onClick: async () => {
                          await handleRemove([row.original.uuid]);
                        },
                      },
                    ]}
                  />
                </Dropdown>
              </ButtonGroup>
            );
          }}
          showRowHover
          onRowClick={(event, row) => {
            if (onRowSelectionChange) onRowSelectionChange({ [row.id]: true });
            onRowSelection(row.original, row.index);
            document.querySelector('#content')?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onRowSelectionChange={onRowSelectionChange}
          rowSelection={rowSelection}
          showSelected
        />
        <ConfirmationDialog />
      </>
    );
  },
);
